import React, { useState, useEffect } from 'react';
import "./components/sgbv-metrics/Metrics.css";
import { Content, Dropdown } from "@carbon/react";
import { DataVis_4 as DataVisualizationIcon, Security, Settings } from "@carbon/react/icons";
import { Link } from "react-router-dom";
import "@carbon/charts/styles.css";
import Metrics from "./components/sgbv-metrics/Metrics";
import { DateFilterInput } from "./components/date-picker/date-picker"; 
import { loadDashboardConfig } from "./config/dashboardConfig";

const HomeComponent = () => {
  const currentDate = new Date();
  const startOfWeek = new Date(currentDate);
  startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
  const endOfWeek = new Date(currentDate);
  endOfWeek.setDate(currentDate.getDate() + (6 - currentDate.getDay()));

  const [dateArray, setDateArray] = useState([startOfWeek, endOfWeek]);
  const [appliedDateArray, setAppliedDateArray] = useState([startOfWeek, endOfWeek]);
  const [selectedDistrict, setSelectedDistrict] = useState("ALL");
  const [districts, setDistricts] = useState(["ALL"]);
  const [districtsLoading, setDistrictsLoading] = useState(true);
  const [dashboardConfig] = useState(loadDashboardConfig);

  useEffect(() => {
    const controller = new AbortController();

    const fetchDistricts = async () => {
      try {
        const response = await fetch(
          'https://sgbv.caringsoulshomecare.com/api/?cmd=getDistricts',
          { signal: controller.signal }
        );
        const data = await response.json();
        if (data.status === "ok") {
          const districtNames = data.results
            .map(item => item.district)
            .filter(district => district && district.toUpperCase() !== "ALL")
            .sort((first, second) => first.localeCompare(second));

          setDistricts(["ALL", ...districtNames]);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error('Error fetching districts:', error);
        }
      } finally {
        if (!controller.signal.aborted) {
          setDistrictsLoading(false);
        }
      }
    };

    fetchDistricts();
    return () => controller.abort();
  }, []);

  const handleDistrictChange = (event) => {
    setSelectedDistrict(event.selectedItem);
  };
 
  const updateDashboardMetrics = () => setAppliedDateArray([...dateArray]);

  const scopeLabel = selectedDistrict === "ALL"
    ? "National aggregate overview"
    : `${selectedDistrict} district overview`;

  return (
    <div className="dashboard-app">
      <header className="metrics-header">
        <div className="brand-lockup">
          <span className="brand-mark"><Security size={24} /></span>
          <div>
            <div className="header-label">{dashboardConfig.dashboardTitle}</div>
            <div className="header-subtitle">{dashboardConfig.dashboardSubtitle}</div>
          </div>
        </div>
        <div className="header-actions">
          <div className="header-context"><DataVisualizationIcon size={18} /> Aggregate analytics</div>
          <Link className="admin-access-link" to="/admin"><Settings size={18} /><span>Admin</span></Link>
        </div>
      </header>
      <Content className="metrics-body">
        <div className="dashboard-intro">
          <div>
            <span className="section-kicker">Monitoring overview</span>
            <h1>{scopeLabel}</h1>
            <p>Explore reporting volume, demographics, response channels, services, and AI-assisted insights.</p>
          </div>
          <span className="privacy-badge"><Security size={16} /> Aggregate data only</span>
        </div>

        <section className="filter-panel" aria-label="Dashboard filters">
          <div className="switcher-date-container">
            <div className="switcher-container-width">
              <Dropdown
                id="district-dropdown"
                items={districts}
                label={districtsLoading ? "Loading districts..." : "District"}
                titleText="District"
                onChange={handleDistrictChange}
                selectedItem={selectedDistrict}
                disabled={districtsLoading}
              />
            </div>
            <div className="date-container-width date-input-container">
              {selectedDistrict === "ALL" ? (
                <div className="aggregate-date-note">
                  <span>Reporting period</span>
                  <strong>All available dates</strong>
                  <small>The national aggregate endpoint does not apply date filters.</small>
                </div>
              ) : (
                <DateFilterInput
                  handleOnChangeRange={setDateArray}
                  updateDashboard={updateDashboardMetrics}
                  dateValue={dateArray}
                />
              )}
            </div>
          </div>
        </section>

        <Metrics district={selectedDistrict} dateArray={appliedDateArray} config={dashboardConfig} />
      </Content>
      <footer className="footer">
        <div><strong>{dashboardConfig.dashboardTitle}</strong><span>{dashboardConfig.footerOrganization}</span></div>
        <span>© 2026 · Aggregate reporting system</span>
      </footer>
    </div>
  );
};

export default HomeComponent;
